import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Select,
  Stack,
  Checkbox,
} from "@chakra-ui/react";
import InputPhone from "@raiden/library/components/Form/InputPhone";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import { COUNTRIES_STATE_VALUE_ENABLED } from "@raiden/library/constants/countries";
import {
  CUSTOMERS_COMPOSITIONS_LIST,
  CUSTOMERS_GENDERS_LIST,
  CUSTOMERS_GENDERS_MESSAGE,
} from "@raiden/library/constants/customers";
import useApiFetcher from "@raiden/library/hooks/useApiFetcher";
import useLocale from "@raiden/library/hooks/useLocale";
import { usePreferredYear } from "@raiden/library/hooks/usePreferredYear";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import useSWR from "swr";
import ApiErrorMessage from "../../../components/ApiErrorMessage";
import InputPassword from "../../../components/Form/InputPassword";
import ObserverRHF from "@raiden/library/components/ReactHookForm/ObserverRHF";

/**
 * @typedef {object} Props
 * @property {boolean} isLoading
 *
 * @param {Props} props
 */
function Form({ isLoading }) {
  const intl = useIntl();

  const apiFetcher = useApiFetcher();

  const { locale } = useLocale();

  const { getValues, setError, clearErrors } = useFormContext();

  const preferredYear = usePreferredYear();

  const referenceTaxIncomeYear = useMemo(() => {
    if (preferredYear) {
      return intl.formatDate(
        dayjs(preferredYear.from).subtract(1, "year").toDate(),
        { year: "numeric" },
      );
    }
    return null;
  }, [intl, preferredYear]);

  const { data: countriesResponse } = useSWR(
    generateApiUri({
      id: "@countries.viewAny",
      query: {
        state: [COUNTRIES_STATE_VALUE_ENABLED],
        per_page: 250,
      },
    }),
    apiFetcher,
  );

  const enabledCountries = useMemo(() => {
    if (countriesResponse?.data) {
      return countriesResponse?.data?.filter?.(
        (country) => country.state === COUNTRIES_STATE_VALUE_ENABLED,
      );
    }
    return [];
  }, [countriesResponse?.data]);

  const validateGenericPasswordInput = useCallback(
    (otherInputName) => {
      return (value) => {
        const password = value;
        const otherPassword = getValues(otherInputName);
        if (
          password.length > 0 &&
          otherPassword.length > 0 &&
          password !== otherPassword
        ) {
          const message = intl.formatMessage({
            defaultMessage: "Les mots de passe ne correspondent pas",
          });
          setError(otherInputName, { message });
          return message;
        }
        clearErrors(otherInputName);
        return true;
      };
    },
    [clearErrors, getValues, intl, setError],
  );

  const validatePassword = useCallback(
    (value) => {
      return validateGenericPasswordInput("_password_confirmation")(value);
    },
    [validateGenericPasswordInput],
  );

  const validatePasswordConfirmation = useCallback(
    (value) => {
      return validateGenericPasswordInput("data.password")(value);
    },
    [validateGenericPasswordInput],
  );

  return (
    <Stack spacing="1rem">
      <Box>
        <ApiErrorMessage />

        <FormController
          name="data.gender"
          label={intl.formatMessage({ defaultMessage: "Civilité" })}
          rules={{ required: true }}
          renderWithFormControl={(fields) => (
            <Select {...fields}>
              <option value="">-</option>

              {CUSTOMERS_GENDERS_LIST.map((gender, index) => (
                <option value={gender} key={index}>
                  {intl.formatMessage(CUSTOMERS_GENDERS_MESSAGE, {
                    gender,
                  })}
                </option>
              ))}
            </Select>
          )}
        />
      </Box>

      <FormController
        name="data.last_name"
        label={intl.formatMessage({ defaultMessage: "Nom" })}
        rules={{ required: true }}
      />

      <FormController
        name="data.first_name"
        label={intl.formatMessage({ defaultMessage: "Prénom" })}
        rules={{ required: true }}
      />

      <FormController
        name="data.birthdate"
        type="date"
        label={intl.formatMessage({ defaultMessage: "Date de naissance" })}
      />

      <FormController
        name="data.composition"
        label={intl.formatMessage({
          defaultMessage: "Situation familiale",
        })}
        renderWithFormControl={(field) => (
          <Select
            {...field}
            placeholder={intl.formatMessage({
              defaultMessage: "Non précisée",
            })}>
            {CUSTOMERS_COMPOSITIONS_LIST.map((composition, index) => (
              <option value={composition.value} key={index}>
                {intl.formatMessage(composition.label)}
              </option>
            ))}
          </Select>
        )}
      />

      <FormController
        name="data.address_1"
        rules={{ required: true }}
        label={intl.formatMessage({ defaultMessage: "Adresse postale" })}
      />

      <FormController
        name="data.address_2"
        label={intl.formatMessage({
          defaultMessage: "Complément d'adresse",
        })}
      />

      <FormController
        name="data.zipcode"
        type="number"
        label={intl.formatMessage({
          defaultMessage: "Code postal",
        })}
        rules={{ required: true }}
      />

      <FormController
        name="data.city"
        label={intl.formatMessage({ defaultMessage: "Ville" })}
        rules={{ required: true }}
      />

      <FormController
        name="data.country_id"
        rules={{ required: true }}
        label={intl.formatMessage({
          defaultMessage: "Pays",
          description: "singular",
        })}
        renderWithFormControl={(fields) => (
          <Select {...fields} placeholder="-">
            {enabledCountries?.map?.((country) => (
              <option value={country.id} key={country.id}>
                {locale && country.name?.[locale]}
              </option>
            ))}
          </Select>
        )}
      />

      <InputPhone
        isRequired={true}
        nameCountry="data.phone_country"
        nameNumber="data.phone_number"
        countries={countriesResponse?.data}
      />

      <FormController
        type="email"
        name="data.email"
        rules={{ required: true }}
        label={intl.formatMessage({
          defaultMessage: "Adresse mail",
        })}
        placeholder={intl.formatMessage({
          defaultMessage: "Adresse mail",
        })}
      />

      <FormController
        name={`_has_children`}
        renderWithFormControl={(field) => (
          <Checkbox
            {...field}
            isChecked={Boolean(field.value)}
            colorScheme="brandPrimary">
            <FormattedMessage defaultMessage="Avez-vous des enfants qui vont participer aux activités du centre Social Louise Michel ?" />
          </Checkbox>
        )}
      />

      <ObserverRHF
        names={["_has_children"]}
        render={({ values: [_has_children] }) => {
          const hasChidlren = Boolean(_has_children);
          return (
            <>
              {hasChidlren && (
                <>
                  <FormController
                    name="data.dependent_children_count"
                    label={intl.formatMessage({
                      defaultMessage: "Nombre d'enfants à charge",
                    })}
                  />

                  <FormController
                    name="data.caf_number"
                    label={intl.formatMessage({
                      defaultMessage: "Numéro d'allocataire CAF",
                    })}
                  />

                  <FormController
                    name="data.caf_coeff"
                    label={intl.formatMessage({
                      defaultMessage: "Quotient familial",
                    })}
                  />

                  <FormController
                    name="data.reference_tax_income"
                    label={intl.formatMessage({
                      defaultMessage: "Revenu fiscal de référence",
                    })}
                  />

                  {referenceTaxIncomeYear && (
                    <Alert status="info">
                      <AlertIcon />

                      <AlertDescription>
                        <FormattedMessage
                          defaultMessage="Vous devez spécifier le revenu fiscal de votre foyer pour l'année {year}."
                          values={{ year: referenceTaxIncomeYear }}
                        />
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </>
          );
        }}
      />

      <FormController
        name="data.password"
        label={intl.formatMessage({
          defaultMessage: "Mot de passe",
        })}
        rules={{
          required: true,
          validate: validatePassword,
        }}
        renderWithFormControl={(field) => (
          <InputPassword
            {...field}
            placeholder={intl.formatMessage({
              defaultMessage: "Confirmation du mot de passe",
            })}
          />
        )}
      />

      <FormController
        name="_password_confirmation"
        label={intl.formatMessage({
          defaultMessage: "Confirmation du mot de passe",
        })}
        rules={{
          required: true,
          validate: validatePasswordConfirmation,
        }}
        renderWithFormControl={(field) => (
          <InputPassword
            {...field}
            placeholder={intl.formatMessage({
              defaultMessage: "Confirmation du mot de passe",
            })}
          />
        )}
      />

      <Button type="submit" isLoading={isLoading} colorScheme="brandSecondary">
        {intl.formatMessage({ defaultMessage: "Je m'inscris" })}
      </Button>
    </Stack>
  );
}

export default Form;
